import { getReadOnlyGnosisSafeContract } from '@/services/contracts/safeContracts'
import { SENTINEL_ADDRESS } from '@safe-global/protocol-kit/dist/src/utils/constants'
import type { ChainInfo, TransactionDetails } from '@safe-global/safe-gateway-typescript-sdk'
import { getTransactionDetails } from '@safe-global/safe-gateway-typescript-sdk'
import type { AddOwnerTxParams, RemoveOwnerTxParams, SwapOwnerTxParams } from '@safe-global/protocol-kit'
import type { MetaTransactionData, SafeTransaction, SafeTransactionDataPartial } from '@safe-global/safe-core-sdk-types'
import extractTxInfo from '../extractTxInfo'
import { getAndValidateSafeSDK } from './sdk'

/**
 * Create a transaction from raw params
 */
export const createTx = async (txParams: SafeTransactionDataPartial, nonce?: number): Promise<SafeTransaction> => {
  if (nonce !== undefined) txParams = { ...txParams, nonce }
  const safeSDK = getAndValidateSafeSDK()
  return safeSDK.createTransaction({ transactions: [txParams] })
}

/**
 * Create a multiSendCallOnly transaction from an array of MetaTransactionData and options
 * If only one tx is passed it will be created without multiSend and without onlyCalls.
 */
export const createMultiSendCallOnlyTx = async (txParams: MetaTransactionData[]): Promise<SafeTransaction> => {
  const safeSDK = getAndValidateSafeSDK()
  return safeSDK.createTransaction({ transactions: txParams, onlyCalls: true })
}

export const createRemoveOwnerTx = async (txParams: RemoveOwnerTxParams): Promise<SafeTransaction> => {
  const safeSDK = getAndValidateSafeSDK()
  return safeSDK.createRemoveOwnerTx(txParams)
}

export const createAddOwnerTx = async (
  chain: ChainInfo,
  isDeployed: boolean,
  txParams: AddOwnerTxParams,
): Promise<SafeTransaction> => {
  const safeSDK = getAndValidateSafeSDK()
  if (isDeployed) return safeSDK.createAddOwnerTx(txParams)

  const safeVersion = await safeSDK.getContractVersion()

  const contract = await getReadOnlyGnosisSafeContract(chain, safeVersion)
  // @ts-ignore
  const data = contract.encode('addOwnerWithThreshold', [txParams.ownerAddress, txParams.threshold])

  const tx = {
    to: await safeSDK.getAddress(),
    value: '0',
    data,
  }

  return safeSDK.createTransaction({
    transactions: [tx],
  })
}

export const createSwapOwnerTx = async (
  chain: ChainInfo,
  isDeployed: boolean,
  txParams: SwapOwnerTxParams,
): Promise<SafeTransaction> => {
  const safeSDK = getAndValidateSafeSDK()
  if (isDeployed) return safeSDK.createSwapOwnerTx(txParams)

  const safeVersion = await safeSDK.getContractVersion()

  const contract = await getReadOnlyGnosisSafeContract(chain, safeVersion)
  // @ts-ignore SwapOwnerTxParams is a union type and the method expects a specific one
  const data = contract.encode('swapOwner', [SENTINEL_ADDRESS, txParams.oldOwnerAddress, txParams.newOwnerAddress])

  const tx = {
    to: await safeSDK.getAddress(),
    value: '0',
    data,
  }

  return safeSDK.createTransaction({
    transactions: [tx],
  })
}

export const createUpdateThresholdTx = async (threshold: number): Promise<SafeTransaction> => {
  const safeSDK = getAndValidateSafeSDK()
  return safeSDK.createChangeThresholdTx(threshold)
}

/**
 * Manual workaround for getModulesPaginated bug in Safe v1.1.1, v1.2.0, v1.3.0
 * Iterates through all pages to get complete module list
 */
const getAllModulesManual = async (safeSDK: any): Promise<string[]> => {
  const safeContract = safeSDK.getContractManager().safeContract
  if (!safeContract) {
    throw new Error('Safe is not deployed')
  }

  const allModules: string[] = []
  let start = SENTINEL_ADDRESS
  const pageSize = 100 // Use large page size to minimize iterations

  while (true) {
    const [modules, next] = await safeContract.getModulesPaginated([start, BigInt(pageSize)])

    // Add modules to our list
    allModules.push(...modules)

    // If next is SENTINEL_ADDRESS or empty, we've reached the end
    if (next === SENTINEL_ADDRESS || next === '0x0000000000000000000000000000000000000000' || !next) {
      break
    }

    // If next is not in the modules array, use it as the new start
    if (!modules.includes(next)) {
      start = next
    } else {
      // If next is in modules, use the last module as start (workaround for the bug)
      start = modules[modules.length - 1]
    }
  }

  return allModules
}

export const createRemoveModuleTx = async (moduleAddress: string): Promise<SafeTransaction> => {
  const safeSDK = getAndValidateSafeSDK()
  const safeVersion = await safeSDK.getContractVersion()

  // Workaround for Safe v1.1.1, v1.2.0 and v1.3.0: getModulesPaginated can miss the last element of a page
  // Use manual pagination to get complete module list and compute the previous module in the linked list
  if (safeVersion === '1.1.1' || safeVersion === '1.2.0' || safeVersion === '1.3.0') {
    const modules = await getAllModulesManual(safeSDK)
    const index = modules.indexOf(moduleAddress)

    if (index === -1) {
      throw new Error('Module provided is not enabled yet')
    }

    const prev = index === 0 ? SENTINEL_ADDRESS : modules[index - 1]

    const safeContract = safeSDK.getContractManager().safeContract
    if (!safeContract) {
      throw new Error('Safe is not deployed')
    }

    // @ts-ignore encode is available on Safe contract wrappers used in this project
    const data = safeContract.encode('disableModule', [prev, moduleAddress])

    const tx = {
      to: await safeSDK.getAddress(),
      value: '0',
      data,
    }

    return safeSDK.createTransaction({ transactions: [tx] })
  }

  // For v1.0.0 and >= v1.4.1, the SDK/contract handles this correctly
  return safeSDK.createDisableModuleTx(moduleAddress)
}

export const createRemoveGuardTx = async (): Promise<SafeTransaction> => {
  const safeSDK = getAndValidateSafeSDK()
  return safeSDK.createDisableGuardTx()
}

/**
 * Create a rejection tx
 */
export const createRejectTx = async (nonce: number): Promise<SafeTransaction> => {
  const safeSDK = getAndValidateSafeSDK()
  return safeSDK.createRejectionTransaction(nonce)
}

/**
 * Prepare a SafeTransaction from Client Gateway / Tx Queue
 */
export const createExistingTx = async (
  chainId: string,
  txId: string,
  txDetails?: TransactionDetails,
): Promise<SafeTransaction> => {
  // Get the tx details from the backend if not provided
  txDetails = txDetails || (await getTransactionDetails(chainId, txId))

  // Convert them to the Core SDK tx params
  const { txParams, signatures } = extractTxInfo(txDetails)

  // Create a tx and add pre-approved signatures
  const safeTx = await createTx(txParams, txParams.nonce)
  Object.entries(signatures).forEach(([signer, data]) => {
    safeTx.addSignature({
      signer,
      data,
      staticPart: () => data,
      dynamicPart: () => '',
      isContractSignature: false,
    })
  })

  return safeTx
}
